import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { OverrideWithReasonField } from "./OverrideWithReasonField";
import { AccuracyFeedbackControl } from "./AccuracyFeedbackControl";
import { InsuranceExtraInfo, type FlatInsuranceFields } from "../utils/extraInfoMapping";
import { useConfirmVerification } from "../hooks/useConfirmVerification";
import { en } from "../i18n/en";
import type { InsuranceOcrJobState } from "../types/InsuranceOcrJob.type";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel";

// TODO(PRO-XXX): upgrade yup to ≥1.x — .when() with multiple deps has a bug in 0.28.3
// that silently ignores some conditions; upgrade and remove this comment when fixed (FE5 §5.13)

interface SubCategoryOption {
  id: number;
  name: string;
}

interface ConfirmExtractedFieldsProps {
  documentId: number;
  /** Raw extra_info from the document API response */
  extraInfo: Record<string, unknown>;
  feedbackSubmittedAt?: string | null;
  isLoading?: boolean;
  ocrJobId?: number | null;
  ocrJobState: InsuranceOcrJobState;
  /** Sub-category the supplier selected when uploading */
  supplierSelectedSubCategory?: SubCategoryOption | null;
  /** Sub-category inferred by Textract */
  textractInferredSubCategory?: SubCategoryOption | null;
  onSuccess?: () => void;
  /** Slot for AccuracyFeedbackControl (Task 024) */
  renderAccuracyFeedback?: () => React.ReactNode;
}

interface FormValues {
  expiry_date: string;
  insured_name: string;
  insurer_name: string;
  overrides: Record<string, string>;
  policy_number: string;
  sub_category_id: string;
  value: string;
}

const buildInitialValues = (
  flat: FlatInsuranceFields,
  extraInfo: Record<string, unknown>,
): FormValues => ({
  // Use OCR-extracted values (from insurance sub-key) when available,
  // then fall back to top-level extra_info (user's previously saved data).
  // Historical docs store values at the top level (e.g. extra_info.expiry_date)
  // without an insurance sub-key, so we must check both locations.
  expiry_date: flat.expiry_date ?? String(extraInfo.expiry_date ?? ""),
  value: flat.value != null ? String(flat.value) : String(extraInfo.value ?? ""),
  insurer_name: flat.insurer_name ?? "",
  insured_name: flat.insured_name ?? "",
  policy_number: flat.policy_number ?? "",
  sub_category_id: flat.sub_category_id != null ? String(flat.sub_category_id) : "",
  overrides: {
    expiry_date: "",
    value: "",
    insurer_name: "",
    insured_name: "",
    policy_number: "",
    sub_category_id: "",
  },
});

const buildValidationSchema = (isFailed: boolean) => {
  // Yup 0.x .when() with cross-field refs causes cyclic dependency errors (toposort).
  // Use a simple .test() on the overrides object instead — no dependency graph needed.
  const overridesSchema = Yup.object().shape({
    expiry_date: Yup.string().notRequired(),
    insurer_name: Yup.string().notRequired(),
    insured_name: Yup.string().notRequired(),
    policy_number: Yup.string().notRequired(),
    sub_category_id: Yup.string().notRequired(),
    value: Yup.string().notRequired(),
  });

  return Yup.object().shape({
    expiry_date: Yup.string().required("Expiry Date is required"),
    insurer_name: Yup.string().required("Insurer Name is required"),
    insured_name: Yup.string().required("Insured Name is required"),
    policy_number: Yup.string().required("Policy Number is required"),
    value: Yup.string().required("Coverage Value is required"),
    sub_category_id: Yup.string().notRequired(),
    // When OCR failed, there are no extracted values to override — no reasons needed.
    // Override reasons only apply when OCR succeeded and the user changes an extracted value.
    overrides: overridesSchema,
  });
};

const SkeletonField: React.FC = () => (
  <div className="animate-pulse mb-4">
    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
    <div className="h-10 bg-gray-200 rounded" />
  </div>
);

export const ConfirmExtractedFields: React.FC<ConfirmExtractedFieldsProps> = ({
  documentId,
  extraInfo,
  ocrJobState,
  ocrJobId,
  feedbackSubmittedAt = null,
  supplierSelectedSubCategory,
  textractInferredSubCategory,
  isLoading = false,
  onSuccess,
  renderAccuracyFeedback,
}) => {
  const isFailed = ocrJobState === "failed";
  const flat = InsuranceExtraInfo.fromApi(extraInfo);
  const initialValues = buildInitialValues(flat, extraInfo);
  const validationSchema = buildValidationSchema(isFailed);

  const confirmMutation = useConfirmVerification({ documentId, onSuccess });

  const showSubCategoryMismatch =
    !isFailed &&
    textractInferredSubCategory != null &&
    supplierSelectedSubCategory != null &&
    textractInferredSubCategory.id !== supplierSelectedSubCategory.id;

  if (isLoading) {
    return (
      <div data-testid="confirm-extracted-fields-skeleton">
        {[1, 2, 3, 4, 5].map((i) => (
          <SkeletonField key={i} />
        ))}
      </div>
    );
  }

  const copy = en.confirm;
  const fieldLabels = copy.fieldLabels;

  return (
    <div data-testid="confirm-extracted-fields">
      <h2 className="text-lg font-semibold mb-1">{copy.title}</h2>
      <p className="text-sm text-gray-600 mb-4">{copy.subtitle}</p>

      {isFailed && (
        <div
          className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 text-sm text-yellow-800"
          data-testid="failed-state-notice"
          role="alert"
        >
          {copy.failedState.notice}
        </div>
      )}

      {showSubCategoryMismatch && (
        <div
          className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-sm text-blue-800"
          data-testid="sub-category-mismatch"
        >
          {copy.subCategoryMismatch.suggest
            .replace("{inferred}", textractInferredSubCategory!.name)
            .replace("{selected}", supplierSelectedSubCategory!.name)}{" "}
          <button
            type="button"
            className="underline font-medium"
            data-testid="sub-category-mismatch-change"
          >
            {copy.subCategoryMismatch.changePrompt}
          </button>
        </div>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          // Date field may be a Date object (from date picker) or string
          let expiryDateStr: string | null = null;
          const rawDate = values.expiry_date as unknown;
          if (rawDate instanceof Date) {
            const yyyy = rawDate.getFullYear();
            const mm = String(rawDate.getMonth() + 1).padStart(2, "0");
            const dd = String(rawDate.getDate()).padStart(2, "0");
            expiryDateStr = `${yyyy}-${mm}-${dd}`;
          } else if (typeof rawDate === "string" && rawDate) {
            expiryDateStr = rawDate;
          }

          // Value field may have commas from formatting — strip them
          const rawValue = values.value ? String(values.value).replace(/[^0-9.]/g, "") : "";

          const flatValues: FlatInsuranceFields = {
            sub_category_id: values.sub_category_id
              ? Number(values.sub_category_id)
              : null,
            expiry_date: expiryDateStr,
            value: rawValue ? Number(rawValue) : null,
            insurer_name: values.insurer_name || null,
            insured_name: values.insured_name || null,
            policy_number: values.policy_number || null,
          };

          const apiValues = InsuranceExtraInfo.toApi(flatValues).insurance as Record<
            string,
            unknown
          >;

          const overrides: Record<string, string> = {};
          for (const [key, reason] of Object.entries(values.overrides)) {
            if (reason) {
              overrides[key] = reason;
            }
          }

          confirmMutation.mutate(
            { values: apiValues, overrides },
            { onSettled: () => setSubmitting(false) }
          );
        }}
      >
        {({ isSubmitting }) => {
          const extractedValues = isFailed
            ? ({} as Record<string, unknown>)
            : {
              expiry_date: flat.expiry_date ?? "",
              value: flat.value != null ? String(flat.value) : "",
              insurer_name: flat.insurer_name ?? "",
              insured_name: flat.insured_name ?? "",
              policy_number: flat.policy_number ?? "",
              sub_category_id:
                flat.sub_category_id != null
                  ? String(flat.sub_category_id)
                  : "",
            };

          return (
            <Form>
              <OverrideWithReasonField
                fieldName="insurer_name"
                extractedValue={extractedValues.insurer_name}
                label={fieldLabels.insurer_name}
                alwaysShowReason={false}
              >
                <div className="mb-1">
                  <FormikInputWithLabel label={fieldLabels.insurer_name} name="insurer_name" data-testid="field-insurer-name" required />
                </div>
              </OverrideWithReasonField>

              <OverrideWithReasonField
                fieldName="insured_name"
                extractedValue={extractedValues.insured_name}
                label={fieldLabels.insured_name}
                alwaysShowReason={false}
              >
                <div className="mb-1">
                  <FormikInputWithLabel label={fieldLabels.insured_name} name="insured_name" data-testid="field-insured-name" required />
                </div>
              </OverrideWithReasonField>

              <OverrideWithReasonField
                fieldName="policy_number"
                extractedValue={extractedValues.policy_number}
                label={fieldLabels.policy_number}
                alwaysShowReason={false}
              >
                <div className="mb-1">
                  <FormikInputWithLabel label={fieldLabels.policy_number} name="policy_number" data-testid="field-policy-number" required />
                </div>
              </OverrideWithReasonField>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <OverrideWithReasonField
                  fieldName="expiry_date"
                  extractedValue={extractedValues.expiry_date}
                  label={fieldLabels.expiry_date}
                  alwaysShowReason={false}
                >
                  <div className="mb-1">
                    <FormikInputWithLabel type="date" name="expiry_date" label={fieldLabels.expiry_date} testId="field-expiry-date" required />
                  </div>
                </OverrideWithReasonField>

                <OverrideWithReasonField
                  fieldName="value"
                  extractedValue={extractedValues.value}
                  label={fieldLabels.value}
                  alwaysShowReason={false}
                >
                  <div className="mb-1">
                    <FormikInputWithLabel
                      label={fieldLabels.value}
                      name="value"
                      type="text"
                      testId="field-value"
                      required
                      thousandsSeparator=","
                    />
                  </div>
                </OverrideWithReasonField>
              </div>

              {confirmMutation.isError && (
                <p className="text-red-600 text-sm mb-3" role="alert">
                  Failed to confirm details. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting || confirmMutation.isPending}
                className="w-full bg-blue-600 text-white rounded-md py-2 px-4 text-sm font-medium disabled:opacity-50 mt-2"
                data-testid="confirm-submit"
              >
                {isSubmitting || confirmMutation.isPending
                  ? copy.submittingButton
                  : copy.submitButton}
              </button>

              {/* Slot for AccuracyFeedbackControl (Task 024) */}
              {renderAccuracyFeedback
                ? renderAccuracyFeedback()
                : ocrJobId != null &&
                (ocrJobState === "succeeded" || ocrJobState === "soft_failed") && (
                  <AccuracyFeedbackControl
                    ocrJobId={ocrJobId}
                    documentId={documentId}
                    feedbackSubmittedAt={feedbackSubmittedAt}
                  />
                )}

            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
